import React from 'react';
import type { PillarDef, PillarResult } from '../lib/pillarScoring';
import PillarCard from './PillarCard';

type Range = 7 | 14 | 30;
const RANGE_OPTIONS: Range[] = [7, 14, 30];

interface Props {
  defs: PillarDef[];
  results: Record<string, PillarResult>;
  deltas?: Record<string, number>;
  onPillarClick: (key: string) => void;
  range?: Range;
  onRangeChange?: (r: Range) => void;
  sparklineData?: Record<string, number[]>;
  /**
   * Render without the outer .card wrapper and internal "Health Pillars"
   * header. Use when PillarGrid is nested inside another section wrapper
   * (e.g. <SystemOverview>) that already provides both.
   * The range toggle is still rendered when in bare mode.
   */
  bare?: boolean;
}

const RangeToggle = ({ range, onRangeChange }: { range: Range; onRangeChange: (r: Range) => void }) => (
  <div className="flex items-center bg-gray-100 dark:bg-dark-card rounded-xl p-1 gap-1">
    {RANGE_OPTIONS.map(r => (
      <button
        key={r}
        onClick={() => onRangeChange(r)}
        className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
          range === r ? 'bg-white dark:bg-dark-surface text-arete-forest dark:text-gray-100 shadow-sm' : 'text-arete-muted dark:text-gray-400 hover:text-arete-forest dark:hover:text-gray-200'
        }`}
      >
        {r}d
      </button>
    ))}
  </div>
);

const Grid = ({ defs, results, deltas, onPillarClick, sparklineData, range }: Props) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-3.5">
    {defs.map(def => (
      <PillarCard
        key={def.key}
        def={def}
        result={results[def.key] ?? { score: null, label: 'No data', color: '#94A3B8', inputs: [], inputCount: 0, subMetric: null }}
        delta={deltas?.[def.key]}
        onClick={() => onPillarClick(def.key)}
        sparkline={sparklineData?.[def.key]}
        rangeDays={range}
      />
    ))}
  </div>
);

export default React.memo(function PillarGrid(props: Props) {
  const { range, onRangeChange, bare } = props;

  // Bare mode: no outer card, no "Health Pillars" header. Used when nested
  // inside SystemOverview which already provides the section frame.
  if (bare) {
    return (
      <div>
        {range != null && onRangeChange && (
          <div className="flex justify-end mb-3">
            <RangeToggle range={range} onRangeChange={onRangeChange} />
          </div>
        )}
        <Grid {...props} />
      </div>
    );
  }

  // Standalone mode: card wrapper + header (backwards compatible for any
  // other callers that still use the original API).
  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="section-label">Health Pillars</h2>
        {range != null && onRangeChange && (
          <RangeToggle range={range} onRangeChange={onRangeChange} />
        )}
      </div>
      <Grid {...props} />
    </div>
  );
});
